import { Box, ButtonBase, Tooltip } from '@mui/material';
import type { ReactNode } from 'react';
import { segmentedControlItemStyles, segmentedControlRailStyles } from './segmentedControlStyles';

export type SegmentedControlItem<Value extends string> = {
  value: Value;
  label: string;
  icon?: ReactNode;
  iconOnly?: boolean;
  disabled?: boolean;
};

export type SegmentedControlProps<Value extends string> = {
  /** 여러 대상의 상태가 섞였으면 null을 전달합니다. */
  value: Value | null;
  items: readonly SegmentedControlItem<Value>[];
  ariaLabel: string;
  controls?: string;
  disabled?: boolean;
  onChange: (value: Value) => void;
};

/** 단일 값 또는 일괄 보기 상태를 선택합니다. 각 버튼의 Tab·Enter·Space 계약을 유지합니다. */
export function SegmentedControl<Value extends string>({ value, items, ariaLabel, controls, disabled = false, onChange }: SegmentedControlProps<Value>) {
  return <Box role="group" aria-label={ariaLabel} sx={{
    display: 'inline-flex', alignItems: 'stretch', flexShrink: 0, maxWidth: '100%',
    ...segmentedControlRailStyles,
    overflowX: 'auto', scrollbarWidth: 'thin',
  }}>
    {items.map((item) => {
      const selected = value === item.value;
      const itemDisabled = disabled || item.disabled;
      const button = <ButtonBase
        key={item.value}
        type="button"
        aria-label={item.label}
        aria-pressed={selected}
        aria-controls={controls}
        data-icon-only={item.iconOnly ? 'true' : undefined}
        disabled={itemDisabled}
        disableRipple
        onClick={() => { if (!selected) onChange(item.value); }}
        sx={segmentedControlItemStyles}
      >
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, '& svg': { fontSize: 20 } }}>
          {item.icon}
          {!item.iconOnly && item.label}
        </Box>
      </ButtonBase>;
      return item.iconOnly && !itemDisabled ? <Tooltip key={item.value} title={item.label}>{button}</Tooltip> : button;
    })}
  </Box>;
}
